import { useState } from "react";
import { Check, Flame } from "lucide-react";
import { toast } from "sonner";
import { putRoutine } from "./clubApi";
import { Card, Pill } from "./ui";

export default function Routine({ d, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const items = d.routine_items || [];
  const done = d.routine_oggi || [];
  if (!items.length) return null;
  const allDone = done.length >= items.length;

  const toggle = async (key) => {
    if (busy) return;
    const next = done.includes(key) ? done.filter((k) => k !== key) : [...done, key];
    setBusy(true);
    try {
      const out = await putRoutine(d.token, d.oggi, next);
      onUpdate(out);
      if (next.length >= items.length && !allDone) toast.success("Routine di oggi completa. Brava, un giorno in più!");
    } catch { toast.error("Non riesco a salvare, riprova"); }
    setBusy(false);
  };

  return (
    <div className="mt-8" data-testid="routine">
      <div className="flex items-center justify-between">
        <p className="font-club-display text-2xl">La tua routine di oggi</p>
        <Pill tone={d.streak > 0 ? "gold" : "muted"} className="whitespace-nowrap shrink-0" data-testid="routine-streak"><Flame size={11} className="mr-1" /> {d.streak} {d.streak === 1 ? "giorno" : "giorni"} di fila</Pill>
      </div>
      <p className="text-sm text-[#9A948A] mt-1">{allDone ? "Tutto fatto per oggi. Domani si ricomincia, con calma." : "Spunta quello che hai fatto. Piccoli gesti, ogni giorno: è questo che cambia le cose."}</p>
      <Card className="mt-4 space-y-1 p-2">
        {items.map((it) => {
          const on = done.includes(it.key);
          return (
            <button key={it.key} type="button" onClick={() => toggle(it.key)} data-testid={`routine-item-${it.key.replace(":", "-")}`}
              className={`w-full flex items-start gap-3 rounded-2xl px-3 py-3 text-left transition-colors ${on ? "bg-[#E5B13A]/10" : "hover:bg-white/[0.03]"}`}>
              <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${on ? "border-[#E5B13A] bg-[#E5B13A] text-[#15120A]" : "border-[#3A3A42]"}`}>{on && <Check size={14} strokeWidth={3} />}</span>
              <span className="min-w-0">
                <span className={`block text-sm font-medium ${on ? "text-[#F5D27A]" : ""}`}>{it.label}</span>
                {it.sotto && <span className="block text-xs text-[#9A948A] mt-0.5">{it.sotto}</span>}
              </span>
            </button>
          );
        })}
      </Card>
      <p className="mt-2 text-xs text-[#6F6A62]">{done.length}/{items.length} oggi · {d.giorni_routine || 0} {d.giorni_routine === 1 ? "giorno completo" : "giorni completi"} in tutto</p>
      {d.routine_settimana && (
        <div className="mt-3 rounded-2xl border border-[#232328] bg-[#141417] px-4 py-3" data-testid="routine-week">
          <div className="flex justify-between">
            {d.routine_settimana.map((g, i) => {
              const isToday = g.giorno === d.oggi;
              const dayLabel = new Date(g.giorno + "T12:00:00").toLocaleDateString("it-IT", { weekday: "short" }).slice(0, 3);
              return (
                <div key={g.giorno} className="flex flex-col items-center gap-1.5" data-testid={`routine-day-${i}`}>
                  <span className={`text-[0.6rem] uppercase tracking-wide ${isToday ? "text-[#F5D27A]" : "text-[#6F6A62]"}`}>{isToday ? "oggi" : dayLabel}</span>
                  <span className={`h-7 w-7 rounded-full flex items-center justify-center border ${g.completo ? "bg-[#E5B13A] border-[#E5B13A] text-[#15120A]" : g.fatti ? "border-[#E5B13A]/60 text-[#F5D27A]" : "border-[#2E2E34] text-[#3A3A42]"} ${isToday ? "ring-2 ring-[#E5B13A]/30" : ""}`}>
                    {g.completo ? <Check size={14} strokeWidth={3} /> : g.fatti ? <span className="text-[0.6rem] font-semibold">{g.fatti}</span> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-[#9A948A] leading-relaxed">Ogni giorno a mezzanotte le spunte ripartono da zero: è normale. Un cerchio pieno è un giorno completo, e i giorni di fila crescono.</p>
        </div>
      )}
    </div>
  );
}
